import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import ConfirmModal from '../../components/modals/ConfirmModal'

interface Amenity {
    _id: string;
    name: string;
    icon: string;
    score: number;
}

export default function AdminAmenities() {
    const { token } = useAuth()
    const [amenities, setAmenities] = useState<Amenity[]>([])
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('')
    const [score, setScore] = useState<number>(0)
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; id: string; name: string }>({ isOpen: false, id: '', name: '' });

    useEffect(() => {
        fetchAmenities()
    }, [])

    const fetchAmenities = async () => {
        try {
            const res = await fetch(`${API_BASE}/amenities`)
            const data = await res.json()
            if (Array.isArray(data)) setAmenities(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImage(file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name || (!image && !editId) || !token) return
        setSubmitting(true)
        setMsg(null)

        let imageUrl = preview

        try {
            if (image) {
                const fd = new FormData()
                fd.append('image', image)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) imageUrl = uploadData.url
                else throw new Error('Image upload failed')
            }

            // 2. Save/Update amenity
            const url = editId ? `${API_BASE}/amenities/${editId}` : `${API_BASE}/amenities`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name, icon: imageUrl, score })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Amenity updated successfully!' : 'Amenity added successfully!' })
                setName('')
                setScore(0)
                setImage(null)
                setPreview('')
                setEditId(null)
                setIsModalOpen(false)
                fetchAmenities()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to add amenity' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async () => {
        const { id } = confirmModal;
        if (!token || !id) return
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await fetch(`${API_BASE}/amenities/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setAmenities(prev => prev.filter(a => a._id !== id))
                setMsg({ type: 'success', text: 'Amenity deleted.' })
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (a: Amenity) => {
        setName(a.name)
        setScore(a.score || 0)
        setPreview(a.icon)
        setImage(null)
        setEditId(a._id)
        setMsg(null)
        setIsModalOpen(true)
    }

    return (
        <AdminLayout>
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#05144b] mb-2 leading-none">Amenities Registry</h1>
                    <p className="text-[#05144b] font-semibold text-base">Manage and synchronize global property amenity assets.</p>
                </div>
                <button 
                    onClick={() => {
                        setEditId(null)
                        setName('')
                        setScore(0)
                        setImage(null)
                        setPreview('')
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold transition-all shadow-xl active:scale-95"
                >
                    <i className="fa-solid fa-plus-circle"></i>
                    <span>Add Amenity</span>
                </button>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-md flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
                    msg.type === 'success' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-rose-50 text-rose-700 border border-rose-100'
                }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
                    <p className="text-sm font-bold">{msg.text}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden min-h-[500px]">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50">
                    <div>
                        <h4 className="text-xl font-bold text-[#05144b] leading-tight">Amenity List</h4>
                        <p className="text-sm font-semibold text-[#05144b] mt-1">{amenities.length} amenities available</p>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-black">
                        <div className="animate-spin w-8 h-8 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-sm font-bold">Synchronizing amenities...</p>
                    </div>
                ) : amenities.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-white dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fa-solid fa-layer-group text-blue-100 dark:text-slate-700 text-2xl"></i>
                        </div>
                        <p className="text-blue-950 dark:text-blue-950 font-medium font-bold">No amenities found</p>
                        <p className="text-sm text-blue-950 mt-1">Add your first utility to get started</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="px-8 py-3.5 text-left text-sm font-bold text-black border-b border-gray-100">Icon</th>
                                    <th className="px-8 py-3.5 text-left text-sm font-bold text-black border-b border-gray-100">Amenity Name</th>
                                    <th className="px-8 py-3.5 text-left text-sm font-bold text-black border-b border-gray-100">Score</th>
                                    <th className="px-8 py-3.5 text-right text-sm font-bold text-black border-b border-gray-100">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 border">
                                {amenities.map(a => (
                                    <tr key={a._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-2.5">
                                            <div className="w-11 h-11 rounded-md bg-white p-1.5 flex items-center justify-center border-2 border-slate-50 shadow-sm group-hover:shadow-md transition-all">
                                                <img 
                                                    src={getImageUrl(a.icon)} 
                                                    alt={a.name} 
                                                    className="w-full h-full object-contain" 
                                                />
                                            </div>
                                        </td>
                                        <td className="px-8 py-2.5">
                                            <span className="text-base font-bold text-black">{a.name}</span>
                                        </td>
                                        <td className="px-8 py-2.5">
                                            <span className="text-[14px] font-bold text-black/60 leading-none">{a.score || 0}</span>
                                        </td>
                                        <td className="px-8 py-2.5 text-right">
                                            <div className="flex justify-end gap-3">
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-lg active:scale-95"
                                                    onClick={() => handleEdit(a)}
                                                >
                                                    <i className="fa-solid fa-pen-nib text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border-2 border-slate-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm active:scale-95"
                                                    onClick={() => setConfirmModal({ isOpen: true, id: a._id, name: a.name })}
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Dialog */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#05144b]/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white">
                            <div className="flex items-center gap-5">
                                <div className="w-12 h-12 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl">
                                    <i className="fa-solid fa-layer-group text-lg"></i>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-black tracking-tight capitalize">
                                        {editId ? 'Edit Amenity' : 'Add Amenity'}
                                    </h2>
                                    <p className="text-sm font-bold text-black/60 mt-1">Amenity configuration system</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-blue-50 transition-all text-blue-950"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-black mb-2">
                                            Amenity Name
                                        </label>
                                         <input 
                                            type="text" 
                                            className="w-full px-6 py-3 rounded-md bg-white border-2 border-blue-900/10 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] text-blue-950 transition-all placeholder:text-blue-900 font-bold italic outline-none shadow-sm text-base"
                                            value={name} 
                                            onChange={e => setName(e.target.value)} 
                                            placeholder="Pool, Gym, etc." 
                                            required 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-black mb-2">
                                            Priority Score
                                        </label>
                                         <input 
                                            type="number" 
                                            className="w-full px-6 py-3 rounded-md bg-white border-2 border-blue-900/10 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] text-blue-950 transition-all placeholder:text-blue-900 font-bold italic outline-none shadow-sm text-base"
                                            value={score} 
                                            onChange={e => setScore(Number(e.target.value))} 
                                            placeholder="0" 
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-black mb-2">
                                        Amenity Icon
                                    </label>
                                    <div className="relative group">
                                         <div className={`
                                            relative h-40 rounded-md border-2 border-dashed transition-all flex flex-col items-center justify-center p-4 overflow-hidden
                                            ${preview ? 'border-[#05144b] bg-blue-50/10' : 'border-blue-900/10 dark:border-slate-800 hover:border-[#05144b]/30 bg-white dark:bg-slate-800/50'}
                                        `}>
                                            {preview ? (
                                                <div className="relative z-10 text-center">
                                                    <img 
                                                        src={getImageUrl(preview)} 
                                                        alt="Preview" 
                                                        className="h-16 w-16 mx-auto rounded-md object-contain bg-white dark:bg-slate-900 p-2 shadow-sm mb-2 border border-slate-50"
                                                    />
                                                    <p className="text-[10px] font-bold text-black">Click to change icon</p>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <div className="w-12 h-12 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-3">
                                                        <i className="fa-solid fa-cloud-arrow-up text-blue-900"></i>
                                                    </div>
                                                    <p className="text-xs font-black text-blue-950 mb-1 italic">Upload Icon</p>
                                                    <p className="text-[10px] text-blue-900 font-black uppercase italic tracking-widest">SVG, PNG or JPG</p>
                                                </div>
                                            )}
                                            <input 
                                                type="file" 
                                                className="absolute inset-0 opacity-0 cursor-pointer z-20"
                                                onChange={handleImageChange} 
                                                required={!editId} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6">
                                    <button 
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 py-4 text-[15px] font-bold text-black/50 hover:text-black transition-colors"
                                    >
                                        Discard
                                    </button>
                                         <button 
                                        className={`
                                            flex-[2] py-4 rounded-md font-bold shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95
                                            ${submitting 
                                                ? 'bg-[#05144b]/50 text-white cursor-not-allowed' 
                                                : 'bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50'}
                                        `}
                                        type="submit" 
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                             <>
                                                <div className="animate-spin h-5 w-5 border-2 border-[#05144b]/30 border-t-[#05144b] rounded-full"></div>
                                                <span>Synchronizing...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fa-solid ${editId ? 'fa-pen-nib' : 'fa-check-circle'}`}></i>
                                                <span>{editId ? 'Update Amenity' : 'Save Amenity'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title="Delete Amenity"
                message={`Are you sure you want to delete the amenity "${confirmModal.name}"? This will remove it from all projects.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText="Delete Amenity"
                cancelText="Cancel"
                type="danger"
            />
        </AdminLayout>
    )
}
